// src/component/AdminMusicPlaybackErrors.js
import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DeleteSweepRoundedIcon from "@mui/icons-material/DeleteSweepRounded";

import {
  doc,
  onSnapshot,
  setDoc,
  serverTimestamp,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  limit,
} from "firebase/firestore";
import { db } from "../api/firebase";
import { useAuth } from "../context/auth";
import { AppThemeContext } from "./AppShell";

export default function AdminMusicPlaybackErrors() {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";
  const textBase = theme.railText || "rgba(40,40,60,0.9)";

  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);

  // スイッチ状態（Firestore appFlags/musicPlaybackErrorLog）
  const [enabled, setEnabled] = useState(false);
  const [loadingFlag, setLoadingFlag] = useState(true);
  const [saving, setSaving] = useState(false);

  // エラー一覧
  const [errors, setErrors] = useState([]);
  const [loadingErrors, setLoadingErrors] = useState(false);

  // isAdmin 判定
  useEffect(() => {
    if (!user) return;
    const ref = doc(db, "users", user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      setIsAdmin(snap.exists() ? Boolean(snap.data()?.isAdmin) : false);
    });
    return () => unsub();
  }, [user]);

  // appFlags/musicPlaybackErrorLog をリアルタイム監視
  useEffect(() => {
    const ref = doc(db, "appFlags", "musicPlaybackErrorLog");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setEnabled(snap.exists() ? Boolean(snap.data()?.enabled) : false);
        setLoadingFlag(false);
      },
      () => {
        setEnabled(false);
        setLoadingFlag(false);
      }
    );
    return () => unsub();
  }, []);

  // スイッチ切替
  const handleToggle = async () => {
    const next = !enabled;
    setSaving(true);
    try {
      await setDoc(
        doc(db, "appFlags", "musicPlaybackErrorLog"),
        { enabled: next, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (e) {
      console.error("Failed to toggle musicPlaybackErrorLog:", e);
    } finally {
      setSaving(false);
    }
  };

  // エラーログ取得
  const fetchErrors = async () => {
    setLoadingErrors(true);
    try {
      const q = query(
        collection(db, "error"),
        where("type", "==", "musicPlayback"),
        orderBy("createdAt", "desc"),
        limit(100)
      );
      const snap = await getDocs(q);
      setErrors(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("Failed to fetch errors:", e);
    } finally {
      setLoadingErrors(false);
    }
  };

  // 初回読み込み
  useEffect(() => {
    fetchErrors();
  }, []);

  const formatDate = (ts) => {
    if (!ts) return "-";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString("ja-JP");
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">管理者のみアクセスできます。</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 }, maxWidth: 900, mx: "auto" }}>
      <Typography
        variant="overline"
        sx={{ letterSpacing: "0.18em", fontSize: 11, opacity: 0.8, color: textBase }}
      >
        ADMIN
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color: textBase, mb: 1 }}>
        音楽再生エラーログ
      </Typography>
      <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)", mb: 3 }}>
        ONにすると、音楽再生に失敗した際のエラー情報が error コレクションに記録されます。
      </Typography>

      {/* スイッチ */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          mb: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={handleToggle}
                disabled={loadingFlag || saving}
                color="primary"
              />
            }
            label={
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                エラーログ取得 {enabled ? "ON" : "OFF"}
              </Typography>
            }
          />
          {(loadingFlag || saving) && <CircularProgress size={20} />}
          <Chip
            label={enabled ? "記録中" : "停止中"}
            size="small"
            color={enabled ? "success" : "default"}
          />
        </Stack>
      </Paper>

      {/* エラー一覧 */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: textBase }}>
            エラー一覧（最新100件）
          </Typography>
          <Tooltip title="再読み込み">
            <IconButton onClick={fetchErrors} disabled={loadingErrors} size="small">
              <RefreshRoundedIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        {loadingErrors ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : errors.length === 0 ? (
          <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.5)", py: 3, textAlign: "center" }}>
            エラーログはありません。
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {errors.map((err) => (
              <Paper
                key={err.id}
                variant="outlined"
                sx={{ p: 2, borderRadius: 2, borderColor: border }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Chip label={err.event || "unknown"} size="small" color="error" variant="outlined" />
                  <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>
                    {formatDate(err.createdAt)}
                  </Typography>
                </Stack>

                {err.trackName && (
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    曲名: {err.trackName}
                  </Typography>
                )}

                {err.userId && (
                  <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)", display: "block", mb: 0.5 }}>
                    UID: {err.userId}
                  </Typography>
                )}

                {err.extra?.message && (
                  <Typography variant="body2" sx={{ color: "error.main", mb: 0.5 }}>
                    {err.extra.message}
                  </Typography>
                )}

                {err.state && (
                  <Typography
                    variant="caption"
                    component="pre"
                    sx={{
                      mt: 1,
                      p: 1,
                      bgcolor: "rgba(0,0,0,0.03)",
                      borderRadius: 1,
                      overflow: "auto",
                      fontSize: 11,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {JSON.stringify(err.state, null, 2)}
                  </Typography>
                )}

                {err.userAgent && (
                  <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.4)", display: "block", mt: 0.5 }}>
                    {err.userAgent}
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
